import RequestMethod from "@/models/client/RequestMethod";
import { crudService, CrudServiceType } from "./crudService";
import { RestClient } from "./restClient";
import RestResponse from "@/models/client/RestResponse";
import { Group } from "@/models/feature/feature";
import type { LegislationSubscriptionCreateRequest } from "@/models/subscriptions/LegislationSubscription";
import type { LegislationSubscriptionRemoveRequest } from "@/models/subscriptions/LegislationSubscription";
import type { LegislationSubscription } from "@/models/subscriptions/LegislationSubscription";
import type { SubscriptionRequest } from "@/models/subscriptions/Subscription";
import type { Subscription } from "@/models/subscriptions/Subscription";
import type { Pagination } from "@/models/base/Pagination";

export type SendGroupMessageBody = {
  title?: string;
  message: string;
};

export type GroupServiceType = CrudServiceType<Group> & {
  removeMember: (groupId: string, userId: string) => RestResponse<string>;
  /** Every group in the tenant. Administrators only — see {@link GroupServiceType.getMyGroups}. */
  getGroups: () => RestResponse<Group[]>;
  /** Only the groups the signed-in user belongs to. Open to every user (LAR-456). */
  getMyGroups: () => RestResponse<Group[]>;
  sendMessage: (
    groupId: string,
    body: SendGroupMessageBody
  ) => RestResponse<void>;
  getGroupLegislationSubscriptions: (
    groupId: string
  ) => RestResponse<LegislationSubscription[]>;
  addGroupLegislationSubscription: (
    groupId: string,
    body: LegislationSubscriptionCreateRequest
  ) => RestResponse<LegislationSubscription>;
  removeGroupLegislationSubscription: (
    groupId: string,
    body: LegislationSubscriptionRemoveRequest
  ) => RestResponse<void>;
  getGroupSubscriptions: (
    groupId: string,
    pageNumber?: number
  ) => RestResponse<Pagination<Subscription>>;
  addGroupSubscription: (
    groupId: string,
    body: SubscriptionRequest
  ) => RestResponse<Subscription>;
  removeGroupSubscription: (
    groupId: string,
    body: SubscriptionRequest
  ) => RestResponse<void>;
};

const groupServices = (client: RestClient, path: string): GroupServiceType => ({
  ...crudService<Group>(client, path),
  getGroups: async () => await client.fetch<Group[]>(RequestMethod.GET, path),
  getMyGroups: async () =>
    await client.fetch<Group[]>(RequestMethod.GET, `${path}/mine`),
  removeMember: async (groupId: string, userId: string) =>
    await client.fetch<string>(
      RequestMethod.PATCH,
      `${path}/${groupId}/remove-member/${userId}`
    ),
  sendMessage: async (groupId: string, body: SendGroupMessageBody) =>
    await client.fetch<void>(
      RequestMethod.POST,
      `${path}/${groupId}/send-message`,
      body
    ),
  getGroupLegislationSubscriptions: async (groupId: string) =>
    await client.fetch<LegislationSubscription[]>(
      RequestMethod.GET,
      `${path}/${groupId}/legislation-subscriptions`
    ),
  addGroupLegislationSubscription: async (
    groupId: string,
    body: LegislationSubscriptionCreateRequest
  ) =>
    await client.fetch<LegislationSubscription>(
      RequestMethod.POST,
      `${path}/${groupId}/legislation-subscription`,
      body
    ),
  removeGroupLegislationSubscription: async (
    groupId: string,
    body: LegislationSubscriptionRemoveRequest
  ) =>
    await client.fetch<void>(
      RequestMethod.DELETE,
      `${path}/${groupId}/legislation-subscription`,
      body
    ),
  getGroupSubscriptions: async (
    groupId: string,
    pageNumber = 1
  ) =>
    await client.fetch<Pagination<Subscription>>(
      RequestMethod.GET,
      `${path}/${groupId}/subscriptions?pageNumber=${pageNumber}&pageSize=200&sortAscending=false`
    ),
  addGroupSubscription: async (groupId: string, body: SubscriptionRequest) =>
    await client.fetch<Subscription>(
      RequestMethod.POST,
      `${path}/${groupId}/subscription`,
      body
    ),
  removeGroupSubscription: async (
    groupId: string,
    body: SubscriptionRequest
  ) =>
    await client.fetch<void>(
      RequestMethod.DELETE,
      `${path}/${groupId}/subscription`,
      body
    ),
});

export default groupServices;
